// ─────────────────────────────────────────────────────────────────────────────
// A chave do banco que vem no login.
//
// Até 22/08/2026 a chave do Supabase era embutida no build: ficava dentro do
// bundle e do instalador, extraível por qualquer um, e trocá-la exigia
// republicar o app inteiro.
//
// Agora o webhook de login do n8n pode devolvê-la junto com o nome do operador.
// Assim ela sai do pacote, alcança só quem autenticou, e passa a ser trocável
// em um lugar só.
//
// Módulo separado, sem React e sem rede, para as regras abaixo poderem ser
// verificadas sob `node` — ver scripts/teste-credenciais-login.mjs.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Lê o bloco de credenciais da resposta do login. `null` quando não veio.
 *
 * TOLERANTE DE PROPÓSITO, em dois sentidos:
 *
 * 1. Ausência não é erro. Sem o bloco, o app usa o que foi embutido no build —
 *    é isso que permite publicar o app e mexer no n8n em qualquer ordem. Se
 *    faltar o bloco fosse falha, as duas coisas teriam de subir no mesmo
 *    minuto, e uma janela de erro entre elas derrubaria o suporte.
 *
 * 2. Aceita os nomes prováveis do campo. O fluxo do n8n é montado à mão e a
 *    resposta do login já varia de formato em outros pontos (ver `autenticar`).
 *    Exigir um nome exato transformaria um typo no n8n num app sem banco, e o
 *    erro apareceria longe da causa.
 *
 * O que NÃO é tolerante: url e chave precisam existir as duas. Uma sem a outra
 * não configura nada, e aceitar meia configuração criaria um cliente apontado
 * para lugar nenhum.
 */
export function credenciaisDaResposta(data) {
  if (!data || typeof data !== 'object') return null;

  const bloco = data.supabase || data.banco || null;

  const url = primeiroTexto(
    bloco && bloco.url, bloco && bloco.URL, data.supabaseUrl
  );
  const chave = primeiroTexto(
    bloco && bloco.chave, bloco && bloco.key, bloco && bloco.anonKey,
    bloco && bloco.publishableKey, data.supabaseKey
  );

  if (!url || !chave) return null;
  return { url, chave };
}

/** O primeiro valor que for texto com conteúdo. '' quando nenhum for. */
function primeiroTexto(...valores) {
  for (const v of valores) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

/**
 * Confere se a chave parece segura para o navegador.
 *
 * Não bloqueia nada — só devolve o diagnóstico, para quem chamar decidir. O
 * ponto é não deixar passar em silêncio o erro que originou tudo isto: uma
 * `service_role` entregue ao cliente, que ignora RLS e abre o projeto inteiro.
 *
 * Reconhece os dois formatos: o JWT legado (com `role` no corpo) e as chaves
 * novas (`sb_publishable_` / `sb_secret_`).
 */
export function avaliarChave(chave) {
  const texto = String(chave || '').trim();
  if (!texto) return { ok: false, motivo: 'vazia', segura: false };

  if (texto.startsWith('sb_secret_')) {
    return { ok: true, motivo: 'secret', segura: false };
  }
  if (texto.startsWith('sb_publishable_')) {
    return { ok: true, motivo: 'publishable', segura: true };
  }

  // JWT legado: o corpo é a segunda parte, em base64url.
  const partes = texto.split('.');
  if (partes.length === 3) {
    try {
      const corpo = JSON.parse(decodificarBase64Url(partes[1]));
      if (corpo && corpo.role === 'service_role') {
        return { ok: true, motivo: 'service_role', segura: false };
      }
      if (corpo && corpo.role) {
        return { ok: true, motivo: corpo.role, segura: true };
      }
    } catch (e) { /* formato inesperado: cai no desconhecido abaixo */ }
  }

  // Não dá para afirmar nada. Não é motivo para recusar — chave de formato novo
  // que ainda não conhecemos entraria aqui, e recusá-la travaria o app à toa.
  return { ok: true, motivo: 'desconhecido', segura: true };
}

/** base64url → texto. Funciona no navegador e sob node. */
function decodificarBase64Url(txt) {
  const base64 = String(txt).replace(/-/g, '+').replace(/_/g, '/');
  const cheio = base64 + '='.repeat((4 - (base64.length % 4)) % 4);

  if (typeof atob === 'function') return atob(cheio);
  // eslint-disable-next-line no-undef
  return Buffer.from(cheio, 'base64').toString('utf8');
}
